import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useState } from "react";
import { useDarkMode } from "usehooks-ts";

import { ErrorBanner } from "@/components/Callout";
import { PaymentAgreement } from "@/components/PaymentAgreement";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { addPaymentMethod, getAccountDetailsQueryOptions } from "@/lib/api";

export function CreditCardDialog() {
  const [addCreditCardDialogOpen, setAddCreditCardDialogOpen] = useState(false);
  const [paymentAgreementDialogOpen, setPaymentAgreementDialogOpen] =
    useState(false);
  const { isDarkMode } = useDarkMode();
  const stripe = useStripe();
  const elements = useElements();
  const { data: accountDetails } = useQuery(getAccountDetailsQueryOptions());
  const mutation = useMutation({
    onSuccess: () => setAddCreditCardDialogOpen(false),
    onError: (error: AxiosError) => console.error(error),
    mutationFn: async (options: { email?: string } = {}) => {
      if (!stripe || !elements) {
        throw new Error("Stripe not initialized");
      }

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        elements,
      });

      if (error) {
        throw new Error(error.message);
      }

      if (!paymentMethod.id) {
        throw new Error("Payment method not created");
      }

      if (!canSpendOnCampaigns && !options.email) {
        throw new Error("Email is required");
      }

      await addPaymentMethod(paymentMethod.id, options.email);
    },
  });

  const canSpendOnCampaigns = accountDetails?.can_spend_on_campaigns;

  return (
    <div className="flex items-center gap-2">
      {accountDetails?.payment_method_on_file?.card && (
        <div className="font-mono text-sm text-slate-normal rounded flex items-center gap-2 justify-center px-2 py-1 bg-slate-6 dark:bg-slatedark-6">
          <span className="capitalize">
            {accountDetails.payment_method_on_file.card.brand}
          </span>
          <span>{accountDetails.payment_method_on_file.card.last4}</span>
        </div>
      )}

      {canSpendOnCampaigns || addCreditCardDialogOpen ? (
        <Dialog
          open={addCreditCardDialogOpen}
          onOpenChange={setAddCreditCardDialogOpen}
        >
          <DialogTrigger asChild>
            {canSpendOnCampaigns ? (
              <Button variant="outline">Change</Button>
            ) : (
              <Button>Add Credit Card</Button>
            )}
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add a Credit Card</DialogTitle>
              <DialogDescription>
                Add a credit card to fund your campaigns.
              </DialogDescription>
            </DialogHeader>
            <Separator />
            {mutation.error && <ErrorBanner error={mutation.error} />}
            <form
              className="flex flex-col gap-8"
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target as HTMLFormElement);
                const email = formData.get("email") as string;
                mutation.mutate({ email });
              }}
            >
              {!canSpendOnCampaigns && (
                <div className="flex flex-col gap-4">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    placeholder="you@example.com"
                    required
                  />
                </div>
              )}

              <div className="flex flex-col gap-4">
                <Label htmlFor="amount">Credit Card</Label>
                <CardElement
                  key={isDarkMode ? "dark" : "light"}
                  className="rounded border border-slate-normal p-3 bg-slate-subtle"
                  options={{
                    style: {
                      base: {
                        color: isDarkMode
                          ? "rgb(237 238 240)"
                          : "rgb(28 32 36)",
                        "::placeholder": {
                          color: isDarkMode
                            ? "rgb(176 180 186)"
                            : " rgb(96 100 108)",
                        },
                      },
                      invalid: {
                        color: isDarkMode ? "rgb(236 93 94)" : "rgb(220 62 66)",
                      },
                    },
                  }}
                />
              </div>

              <div className="flex gap-4">
                <div className="flex-1" />
                <Button
                  onPress={() => setAddCreditCardDialogOpen(false)}
                  variant="outline"
                >
                  Cancel
                </Button>
                <Button type="submit" isLoading={mutation.isPending}>
                  Submit
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      ) : (
        <Dialog
          open={paymentAgreementDialogOpen}
          onOpenChange={setPaymentAgreementDialogOpen}
        >
          <DialogTrigger asChild>
            <Button>Add Credit Card</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Payment Agreement</DialogTitle>
            </DialogHeader>
            <div className="notes-content bg-slate-subtle border border-slate-6 dark:border-slatedark-6 rounded-lg p-4 max-h-[50vh] overflow-auto">
              <PaymentAgreement />
            </div>
            <p>
              By participating in campaigns on Graze's platform, the Sponsor and
              Curator agree to these terms and conditions.
            </p>
            <DialogFooter>
              <Button
                onPress={() => {
                  setPaymentAgreementDialogOpen(false);
                  setAddCreditCardDialogOpen(true);
                }}
              >
                Agree
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
